<template>
  <b-modal
    ref="modal"
    title="Editar Comissão de Apuração"
    size="lg"
    class="committee-modal"
    @ok="onSaveClicked($event)"
  >
    <b-form>
      <div class="committee-selection-container">
        <!-- Dropdown de Comitês Existentes -->
        <div
          v-if="existingCommittees.length > 0"
          class="existing-committees-dropdown"
        >
          <h6 class="section-title">
            <i class="fas fa-users mr-2"></i>
            Comitês Existentes
          </h6>

          <!-- Notificação se todos os comitês estão bloqueados -->
          <div
            v-if="
              existingCommittees.length > 0 &&
              existingCommittees.every((committee) =>
                hasBlockedUsers(committee)
              )
            "
            class="all-committees-blocked-notice"
          >
            <i class="fas fa-exclamation-triangle mr-2"></i>
            <span
              >Todos os comitês disponíveis contêm usuários mencionados e
              bloqueados. Entre em contato com o administrador da empresa para
              criar novos comitês ou desbloquear usuários.</span
            >
          </div>

          <!-- Dropdown de Comitês -->
          <b-form-group
            label="Selecionar Comitê Existente:"
            class="committee-dropdown-group"
          >
            <div class="dropdown-container">
              <b-form-select
                v-model="selectedCommitteeId"
                :options="committeeOptions"
                placeholder="Escolha um comitê existente..."
                class="committee-select"
                @change="onCommitteeSelectChange"
              >
                <template #first>
                  <option value="">-- Escolha um comitê existente --</option>
                </template>
              </b-form-select>

              <b-button
                v-if="selectedCommitteeId"
                variant="outline-secondary"
                size="sm"
                class="clear-dropdown-btn"
                title="Limpar seleção"
                @click="clearCommitteeSelection"
              >
                <i class="fas fa-times"></i>
              </b-button>
            </div>

            <!-- Mensagem quando não há comitês disponíveis -->
            <div
              v-if="committeeOptions.length === 0"
              class="no-committees-message"
            >
              <i class="fas fa-info-circle mr-2"></i>
              <span>Nenhum comitê disponível para seleção.</span>
            </div>
          </b-form-group>
        </div>

        <!-- Divisor -->
        <div v-if="existingCommittees.length > 0" class="divider">
          <span>ou</span>
        </div>

        <!-- Seleção Manual -->
        <div class="manual-selection">
          <h6 class="section-title">
            <i class="fas fa-edit mr-2"></i>
            {{
              existingCommittees.length > 0
                ? "Selecionar Membros Manualmente"
                : "Selecionar Membros"
            }}
          </h6>

          <!-- Configurações de Privacidade -->
          <div
            v-if="companyUsers.length > 0 && masterUser"
            class="privacy-section"
          >
            <div class="privacy-option">
              <b-form-checkbox v-model="hideDenouncerData">
                Ocultar dados do denunciante para os membros do comitê
              </b-form-checkbox>
            </div>
          </div>

          <div class="companies-container">
            <!-- Empresa Principal -->
            <div class="company-section">
              <h6 class="company-name">
                <i class="fas fa-building mr-2"></i>
                {{ complaint.company.fantasyName }}
              </h6>
              <div v-if="companyUsers.length > 0" class="users-list">
                <div
                  v-for="user in companyUsers"
                  :key="user._id"
                  class="user-item"
                  :class="{ 'user-blocked': isUserBlocked(user._id) }"
                >
                  <div class="user-info">
                    <div class="user-avatar">
                      <i class="fas fa-user"></i>
                    </div>
                    <span class="user-name"
                      >{{ user.name }} {{ user.surname }}</span
                    >
                  </div>
                  <div class="user-actions">
                    <b-form-checkbox
                      :checked="userInCommittee(user)"
                      :disabled="
                        userIsCommitteeAdmin(user) || isUserBlocked(user._id)
                      "
                      class="committee-check"
                      @change="changeUserInCommittee(user, $event)"
                    >
                      Membro
                    </b-form-checkbox>
                    <b-form-checkbox
                      :checked="userIsCommitteeAdmin(user)"
                      :disabled="isUserBlocked(user._id)"
                      class="admin-check"
                      @change="changeUserCommitteeAdmin(user, $event)"
                    >
                      Relator
                    </b-form-checkbox>
                    <span
                      v-if="isUserBlocked(user._id)"
                      class="user-blocked-badge"
                    >
                      <i class="fas fa-ban"></i>
                      Bloqueado
                    </span>
                  </div>
                </div>
              </div>
              <div v-else class="empty-state">
                <i class="fas fa-users"></i>
                <p>Nenhum usuário cadastrado</p>
              </div>
            </div>

            <!-- Empresa Gerenciadora -->
            <div v-if="isCompanyManagedByOther" class="company-section">
              <h6 class="company-name">
                <i class="fas fa-building mr-2"></i>
                {{ managedByCompany.fantasyName }}
                <small class="text-muted font-italic ml-1"
                  >(Empresa Gestora)</small
                >
              </h6>
              <div class="users-list">
                <div
                  v-for="user in managedByUsers"
                  :key="user._id"
                  class="user-item"
                  :class="{ 'user-blocked': isUserBlocked(user._id) }"
                >
                  <div class="user-info">
                    <div class="user-avatar">
                      <i class="fas fa-user"></i>
                    </div>
                    <span class="user-name"
                      >{{ user.name }} {{ user.surname }}</span
                    >
                  </div>
                  <div class="user-actions">
                    <b-form-checkbox
                      :checked="userInCommittee(user)"
                      :disabled="
                        userIsCommitteeAdmin(user) || isUserBlocked(user._id)
                      "
                      class="committee-check"
                      @change="changeUserInCommittee(user, $event)"
                    >
                      Membro
                    </b-form-checkbox>
                    <b-form-checkbox
                      :checked="userIsCommitteeAdmin(user)"
                      :disabled="isUserBlocked(user._id)"
                      class="admin-check"
                      @change="changeUserCommitteeAdmin(user, $event)"
                    >
                      Relator
                    </b-form-checkbox>
                    <span
                      v-if="isUserBlocked(user._id)"
                      class="user-blocked-badge"
                    >
                      <i class="fas fa-ban"></i>
                      Bloqueado
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-form>

    <template #modal-footer>
      <div class="modal-actions">
        <b-button variant="light" @click="onCloseClicked"> Cancelar </b-button>
        <div class="action-buttons">
          <b-button
            v-show="companyUsers.length > 0 && profile.role !== 'root'"
            variant="warning"
            @click="onRevokeClicked"
          >
            Revogar Acesso
          </b-button>
          <b-button
            v-show="companyUsers.length > 0"
            variant="primary"
            @click="onSaveClicked"
          >
            Salvar
          </b-button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import {
  getCompany,
  getCompanyUsers,
  getManagedCompanyUsersForCommittee,
} from "../../api/company";
import { updateCommittee, revokeCommitteeAccess } from "../../api/complaint";
import { getCommittees } from "../../api/committee";
import { getRecordMentions } from "../../api/mentions";
import { mapGetters } from "vuex";

export default {
  name: "CommitteeModal",
  props: {
    complaint: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      companyUsers: [],
      managedByUsers: [],
      managedByCompany: {},
      existingCommittees: [],
      mentions: [],
      committee: {
        users: [],
        admin: null,
      },
      selectedCommitteeId: "", // Adicionado para controlar a seleção do comitê
    };
  },
  computed: {
    ...mapGetters(["profile"]),
    masterUser() {
      const role = this.profile.role;
      if (this.complaint && this.complaint.designatedAdmin) {
        return true;
      }
      return role === "root" || role === "company-root";
    },
    headers() {
      const headers = [
        { key: "name", label: "Nome" },
        { key: "committee", label: "Comitê" },
      ];
      if (this.masterUser) {
        headers.push({ key: "adm", label: "Relator" });
      }
      return headers;
    },
    committeeFields() {
      return [
        { key: "name", label: "Nome do Comitê" },
        { key: "users", label: "Usuários" },
        { key: "actions", label: "Ações" },
      ];
    },
    rules() {
      return {
        type: { required: true },
        description: { required: true },
        status: { required: this.event.type === 3 },
      };
    },
    isCompanyManagedByOther() {
      // Uma empresa é considerada "gerenciada por outra" no contexto do modal
      // se o usuário logado for de uma empresa diferente da empresa da denúncia,
      // ou se o usuário for root e a empresa da denúncia tiver uma gestora.
      const complaintCompanyId =
        this.complaint.company._id || this.complaint.company;
      const userCompany = this.profile.company;

      if (userCompany && String(userCompany) !== String(complaintCompanyId)) {
        return true;
      }

      return !userCompany && !!this.complaint.company.managedBy;
    },
    managedByCompanyId() {
      const complaintCompanyId =
        this.complaint.company._id || this.complaint.company;
      const userCompany = this.profile.company;

      if (userCompany && String(userCompany) !== String(complaintCompanyId)) {
        return userCompany;
      }

      return this.complaint.company.managedBy;
    },
    existingCommitteesWithDisplayNames() {
      return this.existingCommittees.map((committee) => ({
        ...committee,
        displayNames: committee.users
          .slice(0, 2)
          .map((user) => this.getUserDisplayName(user))
          .join(", "),
      }));
    },
    committeeOptions() {
      return this.existingCommitteesWithDisplayNames
        .filter((committee) => !this.hasBlockedUsers(committee))
        .map((committee) => ({
          value: committee._id,
          text: committee.name,
        }));
    },
    hideDenouncerData: {
      get() {
        return this.committee.hideDenouncerData || false;
      },
      set(value) {
        this.$set(this.committee, "hideDenouncerData", value);
      },
    },
  },

  async created() {
    const complaintCompanyId =
      this.complaint.company._id || this.complaint.company;
    if (complaintCompanyId) {
      const response = await getCompanyUsers(complaintCompanyId);
      this.companyUsers = response.data;
    }

    if (this.isCompanyManagedByOther && this.managedByCompanyId) {
      // Se estou em uma empresa gerenciada, a "Gestora" é a empresa identificada (profile.company ou managedBy)
      try {
        const response = await getManagedCompanyUsersForCommittee(
          this.managedByCompanyId
        );
        this.managedByUsers = response.data;
        this.managedByCompany = (
          await getCompany(this.managedByCompanyId)
        ).data;
      } catch (error) {
        console.error("Erro ao carregar dados da empresa gestora:", error);
      }
    }

    // Carregar menções da denúncia
    await this.loadMentions();

    // Carregar comitês existentes
    await this.loadExistingCommittees();
  },
  methods: {
    async loadMentions() {
      try {
        const response = await getRecordMentions(
          "complaint",
          this.complaint._id
        );
        this.mentions = response.data;
      } catch (error) {
        console.error("Erro ao carregar menções:", error);
        this.mentions = [];
      }
    },

    async loadExistingCommittees() {
      try {
        const complaintCompanyId =
          this.complaint.company._id || this.complaint.company;
        if (!complaintCompanyId) return;

        // Buscar comitês da empresa da denúncia
        const response = await getCommittees(complaintCompanyId);
        let committees = response.data;

        // Se o usuário logado for de uma empresa diferente (empresa gestora) ou for root vendo uma empresa gerenciada,
        // buscar também os comitês da empresa gestora para permitir seleção
        if (this.isCompanyManagedByOther && this.managedByCompanyId) {
          try {
            const managerResponse = await getCommittees(
              this.managedByCompanyId
            );
            const managerCommittees = managerResponse.data;

            // Marcar comitês da gestora para facilitar identificação (opcional no futuro)
            managerCommittees.forEach((c) => {
              c.isManagerCommittee = true;
              c.name = `[GESTÃO] ${c.name}`;
            });

            // Combinar as listas
            committees = [...committees, ...managerCommittees];
          } catch (err) {
            console.error("Erro ao carregar comitês da empresa gestora:", err);
          }
        }

        this.existingCommittees = committees;
      } catch (error) {
        console.error("Erro ao carregar comitês existentes:", error);
        this.existingCommittees = [];
      }
    },

    onCommitteeSelectChange() {
      const selectedCommittee = this.existingCommittees.find(
        (committee) => committee._id === this.selectedCommitteeId
      );
      if (selectedCommittee) {
        this.selectExistingCommittee(selectedCommittee);
      } else {
        this.clearCommitteeSelection();
      }
    },

    clearCommitteeSelection() {
      this.selectedCommitteeId = "";
      this.$set(this.committee, "users", []);
      this.$set(this.committee, "admin", null);
      this.$set(this.committee, "selectedCommitteeName", null);
      this.$bvToast.toast("Comitê selecionado removido.", {
        title: "Informação",
        toastClass: "custom-toast custom-toast-info",
        variant: "light",
        solid: true,
        autoHideDelay: 5000,
        noCloseButton: false,
      });
    },

    selectExistingCommittee(existingCommittee) {
      try {
        // Mapear usuários do comitê existente para IDs
        const userIds = existingCommittee.users.map((user) => {
          // Verificar se user é um objeto com _id ou se já é um ID
          return typeof user === "object" && user._id ? user._id : user;
        });

        // Definir o comitê atual com base no comitê existente usando $set
        this.$set(this.committee, "users", userIds);
        this.$set(
          this.committee,
          "admin",
          existingCommittee.admin
            ? typeof existingCommittee.admin === "object" &&
              existingCommittee.admin._id
              ? existingCommittee.admin._id
              : existingCommittee.admin
            : null
        );

        // Adicionar o nome do comitê selecionado
        this.$set(
          this.committee,
          "selectedCommitteeName",
          existingCommittee.name
        );

        // Manter a configuração de privacidade se já existir
        if (this.committee.hideDenouncerData === undefined) {
          this.$set(this.committee, "hideDenouncerData", false);
        }

        this.$bvToast.toast(`Comitê "${existingCommittee.name}" selecionado!`, {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
      } catch (error) {
        console.error("Erro ao selecionar comitê existente:", error);
        this.$bvToast.toast("Erro ao selecionar comitê existente.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      }
    },

    isCommitteeSelected(committee) {
      // Verificar se o comitê atual tem os mesmos usuários que o comitê existente
      if (!this.committee.users || !committee.users) return false;

      // Se os tamanhos são diferentes, não são iguais
      if (this.committee.users.length !== committee.users.length) return false;

      // Se não há usuários, verificar se ambos estão vazios
      if (this.committee.users.length === 0) return true;

      // Criar cópias para não mutar os arrays originais
      const currentUserIds = [...this.committee.users].sort();
      const committeeUserIds = committee.users
        .map((user) => (typeof user === "object" && user._id ? user._id : user))
        .sort();

      // Comparar os arrays ordenados
      return currentUserIds.every(
        (id, index) => id === committeeUserIds[index]
      );
    },

    isAnyCommitteeSelected() {
      return this.selectedCommitteeId !== "";
    },

    // Verificar se um usuário está bloqueado por menção
    isUserBlocked(userId) {
      return this.mentions.some((mention) => {
        const mentionUserId =
          typeof mention.user === "object" ? mention.user._id : mention.user;
        const checkUserId = typeof userId === "object" ? userId._id : userId;

        return (
          mentionUserId == checkUserId && mention.blocked && mention.verified
        );
      });
    },

    // Verificar se um comitê contém usuários bloqueados
    hasBlockedUsers(committee) {
      if (!committee.users || committee.users.length === 0) return false;

      return committee.users.some((userId) => {
        const userIdStr = typeof userId === "object" ? userId._id : userId;
        return this.isUserBlocked(userIdStr);
      });
    },

    // Obter lista de usuários bloqueados em um comitê
    getBlockedUsersInCommittee(committee) {
      if (!committee.users || committee.users.length === 0) return [];

      return committee.users
        .filter((userId) => {
          const userIdStr = typeof userId === "object" ? userId._id : userId;
          return this.isUserBlocked(userIdStr);
        })
        .map((userId) => {
          const userIdStr = typeof userId === "object" ? userId._id : userId;
          return this.getUserDisplayName({ _id: userIdStr });
        });
    },

    getUserId(user) {
      return typeof user === "object" && user._id ? user._id : user;
    },

    getUserDisplayName(user) {
      // Se já é um objeto com nome, retornar diretamente
      if (typeof user === "object" && user.name) {
        return `${user.name} ${user.surname || ""}`.trim();
      }

      // Se for apenas um ID, tentar encontrar o usuário
      const userId = typeof user === "object" && user._id ? user._id : user;

      // Procurar na empresa principal
      const foundUser = this.companyUsers.find((u) => u._id === userId);
      if (foundUser) {
        return `${foundUser.name} ${foundUser.surname || ""}`.trim();
      }

      // Se não encontrar na empresa principal, procurar na empresa gerenciada
      if (this.isCompanyManagedByOther && this.managedByUsers.length > 0) {
        const managedUser = this.managedByUsers.find((u) => u._id === userId);
        if (managedUser) {
          return `${managedUser.name} ${managedUser.surname || ""}`.trim();
        }
      }

      return `Usuário ${userId}`;
    },

    validateState(ref) {
      if (
        this.vFields[ref] &&
        (this.vFields[ref].dirty || this.vFields[ref].validated)
      ) {
        return !this.errors.has(ref);
      }
      return null;
    },

    userInCommittee(user) {
      const idx = this.committee.users.indexOf(user._id);
      return idx !== -1;
    },

    changeUserInCommittee(user, value) {
      const idx = this.committee.users.indexOf(user._id);
      if (value && idx === -1) {
        this.committee.users.push(user._id);
      } else if (!value && idx !== -1) {
        this.committee.users.splice(idx, 1);
      }
    },

    userIsCommitteeAdmin(user) {
      return this.committee.admin === user._id;
    },

    changeUserCommitteeAdmin(user, value) {
      if (value) {
        this.committee.admin = user._id;
        const idx = this.committee.users.indexOf(user._id);
        if (idx !== -1) {
          this.committee.users.splice(idx, 1);
        }
      } else if (this.userIsCommitteeAdmin(user)) {
        this.committee.admin = null;
      }
    },

    async onSaveClicked() {
      // Se não há comitê selecionado, limpar o nome
      if (!this.isAnyCommitteeSelected()) {
        this.$set(this.committee, "selectedCommitteeName", null);
      }

      const response = await updateCommittee(
        this.complaint._id,
        this.committee
      );
      this.$bvToast.toast("Comitê atualizado com sucesso!", {
        title: "Sucesso",
        toastClass: "custom-toast custom-toast-success",
        variant: "light",
        solid: true,
        autoHideDelay: 5000,
        noCloseButton: false,
      });
      this.$refs.modal.hide();
      // Emitir a resposta completa da API que contém a denúncia atualizada
      this.$emit("changed", response.data);
    },

    async onRevokeClicked() {
      const response = await revokeCommitteeAccess(this.complaint._id);
      this.$bvToast.toast("Acesso do comitê revogado com sucesso!", {
        title: "Sucesso",
        toastClass: "custom-toast custom-toast-success",
        variant: "light",
        solid: true,
        autoHideDelay: 5000,
        noCloseButton: false,
      });
      this.$refs.modal.hide();
      this.committee.users = [];
      this.committee.admin = null;
      this.committee.selectedCommitteeName = null;
      // Emitir a resposta completa da API que contém a denúncia atualizada
      this.$emit("changed", response.data);
    },

    onCloseClicked() {
      this.$refs.modal.hide();
    },

    show() {
      if (this.complaint.committee) {
        // Usar $set para garantir reatividade
        const committeeData = JSON.parse(
          JSON.stringify(this.complaint.committee)
        );
        this.$set(this, "committee", {
          users: committeeData.users || [],
          admin: committeeData.admin || null,
          hideDenouncerData: committeeData.hideDenouncerData || false,
          selectedCommitteeName: committeeData.selectedCommitteeName || null,
        });

        // Se há um comitê selecionado, encontrar o ID correspondente
        if (committeeData.selectedCommitteeName) {
          const matchingCommittee = this.existingCommittees.find(
            (committee) =>
              committee.name === committeeData.selectedCommitteeName
          );
          if (matchingCommittee) {
            this.selectedCommitteeId = matchingCommittee._id;
          }
        } else {
          this.selectedCommitteeId = "";
        }
      } else {
        // Inicializar com valores padrão
        this.$set(this, "committee", {
          users: [],
          admin: null,
          hideDenouncerData: false,
          selectedCommitteeName: null,
        });
        this.selectedCommitteeId = "";
      }

      this.$refs.modal.show();
    },
  },
};
</script>

<style scoped>
/* Modal Base */
.committee-modal >>> .modal-content {
  border-radius: 8px;
  border: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-width: 800px;
  margin: 0 auto;
}

.committee-modal >>> .modal-header {
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  border-radius: 8px 8px 0 0;
  padding: 1rem 1.5rem;
}

.committee-modal >>> .modal-title {
  font-weight: 600;
  color: #2c3e50;
  font-size: 1.1rem;
}

.committee-modal >>> .modal-body {
  padding: 1.5rem;
  max-height: 70vh;
  overflow-y: auto;
}

.committee-modal >>> .modal-footer {
  border-top: 1px solid #e9ecef;
  padding: 1.25rem 1.5rem;
  background: #f8f9fa;
  border-radius: 0 0 8px 8px;
}

/* Container Principal */
.committee-selection-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Seções */
.section-title {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-size: 1rem;
  display: flex;
  align-items: center;
}

/* Comitês Existentes */
.existing-committees-dropdown {
  margin-bottom: 1rem;
}

.committee-dropdown-group {
  margin-bottom: 1rem;
}

.dropdown-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.committee-select {
  border-radius: 6px;
  border: 1px solid #e9ecef;
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
  color: #495057;
  background-color: white !important;
  flex: 1;
  min-height: 38px;
}

.clear-dropdown-btn {
  border-radius: 6px;
  padding: 0.5rem;
  min-width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.committee-select >>> option {
  padding: 0.5rem;
  font-size: 0.9rem;
}

.committee-select:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.no-committees-message {
  background: #e2e3e5;
  border: 1px solid #d6d8db;
  border-radius: 4px;
  padding: 0.75rem;
  margin-top: 0.75rem;
  color: #495057;
  font-size: 0.85rem;
  display: flex;
  align-items: flex-start;
}

.no-committees-message i {
  color: #6c757d;
  margin-top: 0.1rem;
}

.all-committees-blocked-notice {
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
  color: #856404;
  font-size: 0.9rem;
  display: flex;
  align-items: flex-start;
}

.all-committees-blocked-notice i {
  color: #f39c12;
  margin-top: 0.1rem;
}

/* Divisor */
.divider {
  text-align: center;
  margin: 0.75rem 0;
  position: relative;
}

.divider::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: #e9ecef;
}

.divider span {
  background: white;
  padding: 0 1rem;
  color: #6c757d;
  font-size: 0.85rem;
  font-weight: 500;
  position: relative;
  z-index: 1;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Privacidade */
.privacy-section {
  margin-bottom: 1.5rem;
}

.privacy-option {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 1rem;
  border: 1px solid #e9ecef;
}

/* Seleção Manual */
.manual-selection {
  height: 100%;
}

.companies-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.company-section {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 1.25rem;
}

.company-name {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 1rem;
  font-size: 1rem;
  display: flex;
  align-items: center;
}

.users-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.user-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.user-item.user-blocked {
  background: #f8d7da;
  border-color: #f5c6cb;
  opacity: 0.7;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.user-avatar {
  width: 36px;
  height: 36px;
  background: #007bff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-avatar i {
  color: white;
  font-size: 0.9rem;
}

.user-name {
  font-weight: 500;
  color: #2c3e50;
  font-size: 1rem;
}

.user-actions {
  display: flex;
  gap: 1.5rem;
  align-items: center;
}

.committee-check,
.admin-check {
  margin: 0;
  font-size: 0.9rem;
}

.committee-check >>> .custom-control-label,
.admin-check >>> .custom-control-label {
  font-size: 0.9rem;
  color: #495057;
}

.admin-check >>> .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #28a745;
  border-color: #28a745;
}

.user-blocked-badge {
  background: #dc3545;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.user-blocked-badge i {
  font-size: 0.7rem;
}

/* Estado Vazio */
.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #6c757d;
}

.empty-state i {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #adb5bd;
}

.empty-state p {
  margin: 0;
  font-size: 1rem;
}

/* Footer */
.modal-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.action-buttons {
  display: flex;
  gap: 0.75rem;
}

/* Responsividade */
@media (max-width: 768px) {
  .existing-committees-dropdown {
    margin-bottom: 1rem;
  }

  .manual-selection {
    padding-left: 0;
  }

  .committee-selection-container {
    flex-direction: column;
    gap: 1rem;
  }

  .existing-committees-dropdown,
  .manual-selection {
    width: 100%;
  }

  .divider {
    display: none;
  }

  .dropdown-container {
    flex-direction: column;
    gap: 0.5rem;
  }

  .clear-dropdown-btn {
    align-self: flex-end;
  }

  .user-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .user-actions {
    width: 100%;
    justify-content: space-between;
  }

  .modal-actions {
    flex-direction: column;
    gap: 0.75rem;
  }

  .action-buttons {
    width: 100%;
    justify-content: space-between;
  }
}

/* Scrollbar */
.committee-modal >>> .modal-body::-webkit-scrollbar {
  width: 4px;
}

.committee-modal >>> .modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.committee-modal >>> .modal-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.committee-modal >>> .modal-body::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
